import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

import EventTimeline from "../../components/Shared/EventTimeline";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import Customer from "../../models/Customer";
import { TimelineEntry } from "../../models/EventTimeline";
import { useCustomerHistory } from "../../networking/customer.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";

/**
 * Dot colour per event type. Explicit mapping (not string building) so
 * Tailwind's static scan picks the classes up.
 */
const CUSTOMER_DOT_CLASS_BY_TYPE: { [type: string]: string } = {
	CUSTOMER_CREATED: "bg-blue-500",
	CUSTOMER_UPDATED: "bg-blue-400",
	CUSTOMER_ACTIVATED: "bg-emerald-500",
	CUSTOMER_DEACTIVATED: "bg-red-500",
	CUSTOMER_DELETED: "bg-red-500",
	CONTRACT_CHANGED: "bg-amber-500",
	SUBSIDIARY_CHANGED: "bg-amber-400",
	TAG_ADDED: "bg-indigo-500",
	TAG_REMOVED: "bg-indigo-400",
	USER_ADDED: "bg-purple-500",
	USER_REMOVED: "bg-purple-400",
	DELIVERY_STATE_SET_BY_FORTNOX: "bg-teal-500",
};

interface Props {
	customer: Customer;
}

/**
 * "Händelser" tab — the event timeline for a customer. Fetches on tab
 * activation (RouteTabs mounts only the active tab's content).
 *
 * Tag keys are resolved against the live organisation, falling back to the name
 * snapshotted when the event was written — so a renamed tag type reads
 * correctly and a deleted one still reads.
 */
const CustomerHistoryTab: React.FC<Props> = ({ customer }) => {
	const { t } = useTranslation();
	const { entries, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
		useCustomerHistory({ customerId: customer.id });

	const { data: organisation } = useOrganisation();
	const { subsidiaries } = useSubsidiaries();

	const getLabelValues = useCallback(
		(entry: TimelineEntry) => {
			const args = (entry.args ?? {}) as {
				key?: string;
				name?: string;
				from?: string;
				to?: string;
				fromName?: string;
				toName?: string;
				fromDeleted?: boolean;
				toDeleted?: boolean;
				user?: { id: string; name?: string };
				changes?: { field: string; to?: unknown }[];
			};

			const name = entry.type.startsWith("TAG_")
				? organisation?.customerTagTypes?.find((type) => type.key === args.key)?.name?.sv ??
				  args.name ??
				  args.key
				: args.user?.name ?? args.user?.id ?? args.name;

			// Fortnox released the customer when deliveryManagedBy was cleared
			const releasedByFortnox = !!args.changes?.some(
				(change) => change.field === "invoiceDetails.deliveryManagedBy" && !change.to
			);

			return {
				name: name ?? "",
				deliveryLabel: t(
					releasedByFortnox ? "deliveryMethod.timeline.released" : "deliveryMethod.timeline.set"
				),
				from: resolveSide(entry.type, args.from, args.fromName, args.fromDeleted, subsidiaries, t),
				to: resolveSide(entry.type, args.to, args.toName, args.toDeleted, subsidiaries, t),
			};
		},
		[organisation, subsidiaries, t]
	);

	if (isError)
		return (
			<div className="py-8 text-center text-sm text-red-600">{t("eventTimeline.loadError")}</div>
		);

	if (isLoading || !entries) return null;

	return (
		<div className="max-w-2xl py-4">
			<EventTimeline
				entries={entries}
				i18nPrefix="eventTimeline.customer"
				dotClassByType={CUSTOMER_DOT_CLASS_BY_TYPE}
				getLabelValues={getLabelValues}
				emptyState={t("eventTimeline.noEvents")}
			/>

			{hasNextPage && (
				<div className="mt-6 flex justify-center">
					<ButtonNew
						variant="neutral"
						onClick={() => fetchNextPage()}
						disabled={isFetchingNextPage}
					>
						{t("eventTimeline.showMore")}
					</ButtonNew>
				</div>
			)}
		</div>
	);
};

/**
 * One side of a from/to pair. Contract changes arrive with the name resolved
 * server-side (live name, write-time snapshot as fallback) and a flag when the
 * contract has since been deleted; subsidiary changes carry ids only and are
 * resolved against the current subsidiary list.
 */
function resolveSide(
	type: string,
	id: string | undefined,
	resolvedName: string | undefined,
	deleted: boolean | undefined,
	subsidiaries: { id: string; name: string }[],
	t: TFunction
): string {
	if (!id) return "–";

	if (type === "SUBSIDIARY_CHANGED")
		return subsidiaries.find((subsidiary) => subsidiary.id === id)?.name ?? id;

	const name = resolvedName ?? id;

	return deleted ? `${name} ${t("eventTimeline.deletedSuffix")}` : name;
}

export default CustomerHistoryTab;
